import React, { useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import withStyles from 'react-jss';
import PropTypes from 'prop-types';
import { Table, Popconfirm, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, UndoOutlined } from '@ant-design/icons';

import Breadcrumbs from '../Components/Breadcrumbs';
import { sorter, onFilter, filterDropdown, filterIcon, get } from '../utils';
import Api from '../Api';
import { getWorkers, clearWorkers } from '../Actions';
import { errorNotification, successNotification } from '../Snackbar';
import styles from '../styles';
import constants from '../constants';

const WorkerList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);

  const workers = useSelector(state => state.workers);

  useEffect(() => {
    dispatch(getWorkers());

    return () => {
      dispatch(clearWorkers());
    };
  }, []);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/workers/${id}`);
      successNotification('Worker deleted');
      dispatch(getWorkers());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const onRestore = useCallback(async id => {
    try {
      await Api.put(`/workers/restore/${id}`);
      successNotification('Worker recovered');
      dispatch(getWorkers());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const columns = useMemo(() => {
    const _columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: sorter('name'),
        onFilter: onFilter('name'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        sorter: sorter('email'),
        onFilter: onFilter('email'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        sorter: sorter('phone'),
        onFilter: onFilter('phone'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Address',
        dataIndex: 'address',
        sorter: sorter('address'),
        onFilter: onFilter('address'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'comment',
        sorter: sorter('comment'),
        onFilter: onFilter('comment'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        sorter: sorter('createdBy'),
        onFilter: onFilter('createdBy'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Updated By',
        dataIndex: 'updatedBy',
        sorter: sorter('updatedBy'),
        onFilter: onFilter('updatedBy'),
        filterDropdown,
        filterIcon,
      },
    ];

    if (permissions.includes(constants.permissions.updateWorker)) {
      _columns.push({
        title: 'Update',
        render: record =>
          record.isDeleted === false ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/workers/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null,
      });
    }

    if (permissions.includes(constants.permissions.deleteWorker)) {
      _columns.push({
        title: 'Delete',
        render: record =>
          record.isDeleted === false ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null,
      });
    }

    if (permissions.includes(constants.permissions.recoverWorker)) {
      _columns.push({
        title: 'Recover',
        render: record =>
          record.isDeleted === true ? (
            <Tooltip placement="bottom" title="Recover">
              <Popconfirm
                title="Are you sure?"
                onConfirm={() => onRestore(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <UndoOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null,
      });
    }

    return _columns;
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Worker"
        moduleLink="/workers/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={workers}
          rowKey={useMemo(() => record => record.id, [])}
          pagination={useMemo(
            () => ({
              defaultPageSize: 20,
              position: ['bottomLeft'],
              showTotal: total => `Total ${total} Records`,
            }),
            []
          )}
          scroll={useMemo(() => ({ x: 1200, y: window.innerHeight - 300 }), [])}
        />
      </div>
    </>
  );
};

WorkerList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(WorkerList);
